import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuService } from '../../menu/menu.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { customEncode, isFeatureEnabled } from '@piqet-angular/data';
import { commonEnum, CustomModalService } from '@piqet-angular/shared';
import { ToastrService } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import { AuthenticationService } from 'libs/data/src/lib/services/auth/authentication.service';
import { Subscription } from 'rxjs';
import { FeatureCodes } from '../../../../../shared/src/lib/constants';
import { FeatureConfigService } from 'libs/shared/src/lib/service/feature-config.service';

@Component({
  selector: 'piqet-angular-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements OnInit, OnDestroy {
  menuItems: any = [];
  collapsed = true;
  @Input() toggleNav = false;
  basicGt: string = 'basic guide tour';
  advGt: string = 'advanced reporting tour';
  orgRepGt: string = 'org. footprint tour';
  indicatorManual: string = 'indicator manual';
  dataManual: string = 'data manual';
  tutorialExercise: string = 'tutorial exercise';
  rolesPromise: any;
  isAdvancedTourEnabled: boolean = true;
  FeatureCodes = FeatureCodes;
  private subscription!: Subscription;

  // Advanced menu feature flags
  advancedFeatures = {
    addInternalData: true,
    orgPackagingFootprint: true,
    exportLogs: true,
    import: true,
    projectReportSettings: true,
  };

  constructor(
    public menuService: MenuService,
    private route: Router,
    public sharedService: SharedService,
    private modal: CustomModalService,
    private toaster: ToastrService,
    private authenticateService: AuthenticationService,
    private cookieService: CookieService,
    private featureConfigService: FeatureConfigService
  ) {
    this.setMenu();
  }

  ngOnInit() {
    this.subscription = this.menuService.menuAction$.subscribe(() => {
      this.setMenu();
    });
    this.isAdvancedTourEnabled = isFeatureEnabled(FeatureCodes.HELP_ADVANCED_GUIDED_TOUR);
    this.checkAdvancedFeatures();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  checkAdvancedFeatures() {
    this.advancedFeatures = {
      addInternalData: isFeatureEnabled(FeatureCodes.ADVANCED_ADD_INTERNAL_DATA),
      orgPackagingFootprint: isFeatureEnabled(FeatureCodes.ADVANCED_ORG_PACKAGING_FOOTPRINT_REPORT),
      exportLogs: isFeatureEnabled(FeatureCodes.ADVANCED_EXPORT_LOGS),
      import: isFeatureEnabled(FeatureCodes.ADVANCED_IMPORT),
      projectReportSettings: isFeatureEnabled(FeatureCodes.ADVANCED_PROJECT_REPORT_SETTINGS),
    };
  }

  async setMenu() {
    // First, get roles and permissions
    if (!this.rolesPromise) {
      this.rolesPromise = await this.authenticateService.accessRoles();
      localStorage.setItem('access', JSON.stringify(this.rolesPromise.data?.menus));
      localStorage.setItem('featureAccess', JSON.stringify(this.rolesPromise.data?.featureAccess));
      const hideFeatureUpgrade = this.rolesPromise.data?.hideFeatureUpgrade;
      this.featureConfigService.handleHideFeatureUpgrade(hideFeatureUpgrade);
    }

    const res = await this.rolesPromise;

    // Use backend menu structure from API
    if (res?.data?.hierarchicalMenus && res.data.hierarchicalMenus.length > 0) {
      this.menuItems = this.transformBackendMenu(res.data.hierarchicalMenus);
    }

    // Check for org footprint removal
    let removeOrgFootprint = false;
    const replicaMenu = res?.data?.hierarchicalMenus ?? res?.data?.menus;
    if (replicaMenu) {
      replicaMenu.forEach((element: any) => {
        // 110 : Org. footprint module
        if ((element.moduleId === 110 && element.isOverview) || res.length == 0) {
        } else {
          removeOrgFootprint = true;
        }
      });
    }

    if (removeOrgFootprint && this.menuItems.length > 0) {
      this.menuItems = this.menuItems?.map((item: any) => {
        if (item.text === 'Help') {
          item.submenu = item.submenu?.map((subitem: any) => {
            if (subitem.text === 'Guided tour') {
              subitem.submenu = subitem.submenu?.filter(
                (guidedTourItem: any) => guidedTourItem.text !== 'Org. footprint tour'
              );
            }
            return subitem;
          });
        }
        return item;
      });
    }

    // Update Advanced menu items with feature lock status
    this.checkAdvancedFeatures();
    this.updateAdvancedMenuFeatures();
    this.updateGuidedTourFeatures();

    this.isAdvancedTourEnabled = isFeatureEnabled(FeatureCodes.HELP_ADVANCED_GUIDED_TOUR);
  }

  // Transform backend menu structure to match existing format
  private transformBackendMenu(hierarchicalMenus: any[]): any[] {
    const transformedMenu: any[] = [];

    // Define the desired order based on the image
    const menuOrder = [
      'Dashboard',
      'Projects',
      'Custom',
      'Preferences',
      'Administrator',
      'Advanced',
      'Help',
    ];

    // Create a map for quick lookup
    const menuMap = new Map();
    hierarchicalMenus.forEach(menu => {
      const menuName = this.normalizeTopLevelMenuName(menu.moduleName);
      menuMap.set(menuName, menu);
    });

    // Build menu in the desired order
    menuOrder.forEach(menuName => {
      const backendMenu = menuMap.get(menuName);
      if (backendMenu?.isOverview) {
        const menuItem: any = {
          text: menuName,
          link: this.getMenuLink(backendMenu.moduleLink, menuName),
          isAccess: backendMenu.isOverview,
          hideForNav: false,
        };

        // Handle submenu - Skip submenus for Projects as per requirement
        if (menuName === 'Projects') {
          // For Projects, explicitly set no submenu
          menuItem.submenu = null;
        } else if (backendMenu.subMenus && backendMenu.subMenus.length > 0) {
          menuItem.submenu = this.transformSubmenu(backendMenu.subMenus, menuName);
        }

        transformedMenu.push(menuItem);
      }
    });

    // Filter out Products if it exists
    return transformedMenu.filter((item: any) => item.text !== 'Products');
  }

  // Helper method to normalize top-level menu names
  private normalizeTopLevelMenuName(moduleName: string): string {
    return moduleName;
  }

  // Helper method to get proper menu link
  private getMenuLink(moduleLink: string, menuName: string): string {
    if (!moduleLink || moduleLink === '#') {
      const linkMap: { [key: string]: string } = {
        Dashboard: '/dashboard',
        Projects: '/projects',
        Custom: '#',
        Preferences: '#',
        Administrator: '#',
        Advanced: '#',
        Help: '#',
      };
      return linkMap[menuName] || '#';
    }

    // Ensure links start with /
    return moduleLink.startsWith('/') ? moduleLink : `/${moduleLink}`;
  }

  // Helper method to transform submenu recursively
  private transformSubmenu(subMenus: any[], parentName: string): any[] {
    return subMenus
      .filter((submenu: any) => submenu.isOverview)
      .map((submenu: any) => {
        const submenuItem: any = {
          text: this.normalizeSubmenuName(submenu.moduleName, parentName),
          link: this.getMenuLink(submenu.moduleLink, submenu.moduleName),
          isAccess: submenu.isOverview,
          hideForNav: false,
        };

        // Handle nested submenus (level 2 and 3)
        if (submenu.subMenus && submenu.subMenus.length > 0) {
          submenuItem.submenu = this.transformSubmenu(submenu.subMenus, submenu.moduleName);
        }

        // Apply feature lock status for Advanced menu items
        if (parentName === 'Advanced') {
          this.applyAdvancedFeatureLock(submenuItem);
        }

        // Apply feature lock for Help > Guided tour > Advanced reporting tour
        if (parentName === 'Help' && submenu.moduleName === 'Guide tour') {
          submenuItem.text = 'Guide tour'; // Normalize the name
          if (submenuItem.submenu) {
            submenuItem.submenu = submenuItem.submenu.map((tourItem: any) => {
              if (tourItem.text === 'Advanced reporting tour') {
                tourItem.isFeatureLocked = !this.isAdvancedTourEnabled;
                tourItem.featureCode = FeatureCodes.HELP_ADVANCED_GUIDED_TOUR;
              }
              return tourItem;
            });
          }
        }

        return submenuItem;
      });
  }

  // Helper to normalize submenu names
  private normalizeSubmenuName(moduleName: string, parentName: string): string {
    // Special cases for submenu names
    if (parentName === 'Help' && moduleName === 'Guide tour') {
      return 'Guide tour';
    }

    if (parentName === 'Advanced') {
      // Normalize Advanced menu item names to match existing functionality
      if (moduleName === 'Export Logs') {
        return 'Export logs';
      }

      return moduleName;
    }

    return moduleName;
  }

  // Apply feature lock status for Advanced menu items
  private applyAdvancedFeatureLock(menuItem: any): void {
    switch (menuItem.text) {
      case 'Add internal data':
        menuItem.isFeatureLocked = !this.advancedFeatures.addInternalData;
        menuItem.featureCode = FeatureCodes.ADVANCED_ADD_INTERNAL_DATA;
        break;
      case 'Org. packaging footprint report':
        menuItem.isFeatureLocked = !this.advancedFeatures.orgPackagingFootprint;
        menuItem.featureCode = FeatureCodes.ADVANCED_ORG_PACKAGING_FOOTPRINT_REPORT;
        break;
      case 'Export logs':
      case 'Export Logs':
        menuItem.isFeatureLocked = !this.advancedFeatures.exportLogs;
        menuItem.featureCode = FeatureCodes.ADVANCED_EXPORT_LOGS;
        break;
      case 'Import':
        menuItem.isFeatureLocked = !this.advancedFeatures.import;
        menuItem.featureCode = FeatureCodes.ADVANCED_IMPORT;
        // Also apply to Import submenu items
        if (menuItem.submenu) {
          menuItem.submenu = menuItem.submenu.map((importItem: any) => ({
            ...importItem,
            isFeatureLocked: !this.advancedFeatures.import,
            featureCode: FeatureCodes.ADVANCED_IMPORT,
          }));
        }
        break;
      case 'Project report settings':
        menuItem.isFeatureLocked = !this.advancedFeatures.projectReportSettings;
        menuItem.featureCode = FeatureCodes.ADVANCED_PROJECT_REPORT_SETTINGS;
        break;
    }
  }

  updateAdvancedMenuFeatures() {
    this.menuItems = this.menuItems?.map((item: any) => {
      if (item.text === 'Advanced') {
        item.submenu = item.submenu?.map((subitem: any) => {
          switch (subitem.text) {
            case 'Add internal data':
              subitem.isFeatureLocked = !this.advancedFeatures.addInternalData;
              subitem.featureCode = FeatureCodes.ADVANCED_ADD_INTERNAL_DATA;
              break;
            case 'Org. packaging footprint report':
              subitem.isFeatureLocked = !this.advancedFeatures.orgPackagingFootprint;
              subitem.featureCode = FeatureCodes.ADVANCED_ORG_PACKAGING_FOOTPRINT_REPORT;
              break;
            case 'Export logs':
              subitem.isFeatureLocked = !this.advancedFeatures.exportLogs;
              subitem.featureCode = FeatureCodes.ADVANCED_EXPORT_LOGS;
              break;
            case 'Import':
              subitem.isFeatureLocked = !this.advancedFeatures.import;
              subitem.featureCode = FeatureCodes.ADVANCED_IMPORT;
              if (subitem.submenu) {
                subitem.submenu = subitem.submenu.map((importItem: any) => ({
                  ...importItem,
                  isFeatureLocked: !this.advancedFeatures.import,
                  featureCode: FeatureCodes.ADVANCED_IMPORT,
                }));
              }
              break;
            case 'Project report settings':
              subitem.isFeatureLocked = !this.advancedFeatures.projectReportSettings;
              subitem.featureCode = FeatureCodes.ADVANCED_PROJECT_REPORT_SETTINGS;
              break;
          }
          return subitem;
        });
      }
      return item;
    });
  }

  updateGuidedTourFeatures() {
    this.menuItems = this.menuItems?.map((item: any) => {
      if (item.text === 'Help' && item.submenu) {
        item.submenu = item.submenu.map((subitem: any) => {
          if (subitem.text === 'Guide tour' && subitem.submenu) {
            subitem.submenu = subitem.submenu.map((tourItem: any) => {
              if (tourItem.text === 'Advanced reporting tour') {
                tourItem.isFeatureLocked = !this.isAdvancedTourEnabled;
                tourItem.featureCode = FeatureCodes.HELP_ADVANCED_GUIDED_TOUR;
              }
              return tourItem;
            });
          }
          return subitem;
        });
      }
      return item;
    });
  }

  onFeatureLockedClick(featureName: string, featureCode: string) {
    return;
  }

  getManualPdfUrl(text: string): string {
    switch (text?.toLowerCase()) {
      case this.tutorialExercise:
        return 'assets/manuals/PIQET 5.0 - Tutorial_2024.pdf';
      case this.indicatorManual:
        return 'assets/manuals/PIQET Indicator manual_5.1.pdf';
      case this.dataManual:
        return 'assets/manuals/PIQET Data Manual_v5.1.pdf';
      default:
        return '';
    }
  }

  getStaticAssetUrl(menuItem: any): string {
    const byText = this.getManualPdfUrl(menuItem?.text);
    if (byText) return byText;
    const link: string = menuItem?.link || '';
    if (
      link &&
      link !== '#' &&
      (link.toLowerCase().endsWith('.pdf') ||
        link.startsWith('assets/') ||
        link.startsWith('/assets/'))
    ) {
      return link.startsWith('/') ? link.substring(1) : link;
    }
    return '';
  }

  OnGuidetour(event: any) {
    switch (event?.toLowerCase()) {
      case this.tutorialExercise:
        this.route.navigate([]);
        window.open('assets/manuals/PIQET 5.0 - Tutorial_2024.pdf', '_blank');
        break;
      case this.basicGt:
        localStorage.setItem('tourName', 'manual');
        this.route.navigate(['/dashboard'], { queryParams: { haveTour: true } });
        break;
      case this.advGt:
        if (!this.isAdvancedTourEnabled) {
          this.toaster.warning(
            'This feature is not available in your current plan',
            'Feature Restricted'
          );
          return;
        }
        const projId = Number(this.cookieService.get('gT_PackageId'));
        localStorage.setItem('tourName', 'manual');
        this.sharedService.startTourFun(true, 'init');
        if (this.route.url.includes('/interpretation')) {
          this.route.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.route.navigate(
              [
                `projects/projects-setting/${customEncode(
                  btoa(encodeURIComponent(projId))
                )}/interpretation`,
              ],
              { queryParams: { qPrm: 'VndTh9aVG', isTour: 'QOhsaPXQy', mTr: '5JZCI6ImFiY' } }
            );
          });
        } else {
          this.route.navigate(
            [
              `projects/projects-setting/${customEncode(
                btoa(encodeURIComponent(projId))
              )}/interpretation`,
            ],
            {
              queryParams: { qPrm: 'MCXZAPnfsdQnxc', isTour: 'nICsdQOhsaPXQy', mTr: '5JZCI6ImFiY' },
            }
          );
        }
        break;
      case this.orgRepGt:
        this.route.navigate([`/organization-report/upload-sku`], {
          queryParams: { orgTour: 'xICqpZQmdXTy' },
        });
        break;
      case this.indicatorManual:
        this.route.navigate([]);
        window.open('assets/manuals/PIQET Indicator manual_5.1.pdf', '_blank');
        break;
      case this.dataManual:
        this.route.navigate([]);
        window.open('assets/manuals/PIQET Data Manual_v5.1.pdf', '_blank');
        break;
    }
  }

  onSubMenuItemClick(eve: any, menuItem?: any) {
    // Check if the menu item is feature locked
    if (menuItem?.isFeatureLocked) {
      this.onFeatureLockedClick(eve, menuItem.featureCode);
      return;
    }

    if (eve === commonEnum.reportABug) {
      this.route.navigate([]);
      this.modal.invoke('reportBug', {
        initialState: {},
        class: 'modal-xl',
      });
    } else {
      if (eve !== 'Other resources') return;
      this.route.navigate([]);
      window.open('https://piqet.com/support-articles');
    }
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  OnProfilePage() {
    this.route.navigate(['/my-profile']);
  }
}
